/**
 * API Configuration
 * 
 * Centralized configuration for API endpoints.
 * Supports different environments: development, staging, production.
 * 
 * Environment Variables:
 * - VITE_API_URL: Full API base URL (e.g., https://api.example.com)
 * - VITE_ENV: Environment name (development, staging, production)
 */

// Detect environment
const getEnvironment = () => {
  // Explicit environment variable takes precedence
  if (import.meta.env.VITE_ENV) {
    return import.meta.env.VITE_ENV;
  }

  // Vite mode fallback
  if (import.meta.env.PROD) {
    return 'production';
  }

  return 'development';
};

const environment = getEnvironment();
const isDevelopment = environment === 'development';
const isProduction = environment === 'production';
const isStaging = environment === 'staging';

/**
 * Get the API base URL for the current environment
 * 
 * Priority:
 * 1. VITE_API_URL environment variable (explicit override)
 * 2. Default based on environment
 */
const getApiBaseUrl = () => {
  // Explicit API URL takes highest priority
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Development defaults
  if (isDevelopment) {
    // Use relative path to leverage Vite dev proxy
    // This avoids CORS issues during local development
    return '/api';
  }

  // Staging/Production - should be set via environment variable
  // Fallback to relative path if not configured
  console.warn('VITE_API_URL not set. Using relative /api path.');
  return '/api';
};

// Export configuration
export const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  environment,
  isDevelopment,
  isProduction,
  isStaging,
  
  // Request defaults
  timeout: 30000, // 30 seconds
  retryAttempts: isDevelopment ? 0 : 3,
  
  // Headers that should be included with every request
  defaultHeaders: {
    'Content-Type': 'application/json'
  }
};

/**
 * Build a full API URL for an endpoint
 * @param {string} endpoint - API endpoint path (e.g., '/users', '/datasets')
 * @returns {string} Full API URL
 */
export const buildApiUrl = (endpoint) => {
  const base = API_CONFIG.baseUrl;
  // Ensure endpoint starts with /
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  // Avoid double slashes
  if (base.endsWith('/')) {
    return `${base.slice(0, -1)}${path}`;
  }
  return `${base}${path}`;
};

/**
 * Get authorization headers for API requests
 * @param {string} idToken - Firebase ID token
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeaders = (idToken) => {
  if (!idToken) {
    return { ...API_CONFIG.defaultHeaders };
  }
  return {
    ...API_CONFIG.defaultHeaders,
    'Authorization': `Bearer ${idToken}`
  };
};

export default API_CONFIG;

